import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { Todo } from '@/infra/types/domain';
import { apiClient } from '@/infra/api';
import { removeTodo } from '@/features/todos/ducks';

// CommitmentRejectSection — below-actions detail slot renderer for the
// commitments plugin. For todos auto-created by lifedb (source_plugin ===
// 'commitments') it shows a "This was wrongly created" button. Clicking it
// confirms, POSTs to /api/v1/commitments/{id}/reject (the server soft-deletes
// the todo locally and proxies the rejection to lifedb so it labels its AI
// activity), then removes the row from the list and closes the modal.
//
// Renders null for every other todo so the slot is a no-op outside commitments.
export function CommitmentRejectSection({ todo }: { todo: Todo }) {
  const dispatch = useDispatch();
  const [, setSearchParams] = useSearchParams();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Strip ?todo= so the modal collapses — mirrors TodoModal.close(), but the
  // slot has no onClose handle so it drives the URL directly.
  const closeModal = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('todo');
      return next;
    });
  }, [setSearchParams]);

  const onReject = useCallback(async () => {
    if (pending) return;
    const ok = window.confirm(
      'Mark this as wrongly created? It will be deleted and lifedb will be told its detection was wrong.',
    );
    if (!ok) return;
    setPending(true);
    setError(null);
    try {
      await apiClient.post(`/api/v1/commitments/${todo.id}/reject`);
      // Optimistically drop the row from the list, then dismiss the modal.
      dispatch(removeTodo(todo.id));
      closeModal();
    } catch (e) {
      setError('Could not reject this todo. Please try again.');
      setPending(false);
    }
  }, [pending, todo.id, dispatch, closeModal]);

  if (todo.source_plugin !== 'commitments') return null;

  return (
    <div className="todo-detail__commitment-section" data-field="commitment-reject">
      <button
        type="button"
        className="todo-detail__commitment-reject-btn"
        onClick={onReject}
        disabled={pending}
        aria-busy={pending}
      >
        {pending ? 'Rejecting…' : 'This was wrongly created'}
      </button>
      {error && (
        <div className="todo-detail__commitment-error" role="alert">
          {error}
        </div>
      )}
    </div>
  );
}
